import {
  type CopilotAction,
  type CopilotDataAnalysis,
  type CopilotDataFetch,
  type CopilotThinkingSignature,
  type CopilotThinkingSignatureType
} from './types'

export type CardActionSignature = CopilotAction | CopilotDataFetch | CopilotDataAnalysis
export type ActionGroupType = CardActionSignature['type']
export type NonActionThinkingSignature = Exclude<CopilotThinkingSignature, CardActionSignature>

interface SummaryLabels {
  singular: string
  plural: string
}

interface ActionGroupConfig extends SummaryLabels {
  keyPrefix: string
  inProgressTitle: string
  completeTitle: string
}

const THINKING_TYPE_TO_SUMMARY_TEXT: Record<CopilotThinkingSignatureType, SummaryLabels> = {
  thought: {
    singular: 'thought',
    plural: 'thoughts'
  },
  search: {
    singular: 'search',
    plural: 'searches'
  },
  url_open: {
    singular: 'link',
    plural: 'links'
  },
  inspect_page: {
    singular: 'page element',
    plural: 'page elements'
  },
  inspect_source: {
    singular: 'discovery',
    plural: 'discoveries'
  },
  action: {
    singular: 'action',
    plural: 'actions'
  },
  data_fetch: {
    singular: 'report',
    plural: 'reports'
  },
  data_analysis: {
    singular: 'dataset',
    plural: 'datasets'
  }
}

const ACTION_GROUP_CONFIG: Record<ActionGroupType, ActionGroupConfig> = {
  action: {
    ...THINKING_TYPE_TO_SUMMARY_TEXT.action,
    keyPrefix: 'page-action-group',
    inProgressTitle: 'Taking',
    completeTitle: 'Took'
  },
  data_fetch: {
    ...THINKING_TYPE_TO_SUMMARY_TEXT.data_fetch,
    keyPrefix: 'data-fetch-group',
    inProgressTitle: 'Fetching',
    completeTitle: 'Fetched'
  },
  data_analysis: {
    ...THINKING_TYPE_TO_SUMMARY_TEXT.data_analysis,
    keyPrefix: 'data-analysis-group',
    inProgressTitle: 'Analysing',
    completeTitle: 'Analysed'
  }
}

export type ThinkingSignatureRenderItem =
  | { kind: 'signature'; signature: NonActionThinkingSignature }
  | { kind: 'action_group'; groupType: ActionGroupType; actions: CardActionSignature[] }

const isCardActionSignature = (signature: CopilotThinkingSignature): signature is CardActionSignature =>
  signature.type in ACTION_GROUP_CONFIG

export const groupThinkingSignaturesForRender = (
  signatures: CopilotThinkingSignature[]
): ThinkingSignatureRenderItem[] => {
  const result: ThinkingSignatureRenderItem[] = []
  let index = 0

  while (index < signatures.length) {
    const signature = signatures[index]

    if (!isCardActionSignature(signature)) {
      result.push({ kind: 'signature', signature })
      index++
      continue
    }

    const groupType = signature.type
    const actions: CardActionSignature[] = []

    while (index < signatures.length) {
      const candidate = signatures[index]
      if (!isCardActionSignature(candidate) || candidate.type !== groupType) {
        break
      }

      actions.push(candidate)
      index++
    }

    result.push({ kind: 'action_group', groupType, actions })
  }

  return result
}

export const constructThinkingSignaturesSummary = (thinkingSignatures: CopilotThinkingSignature[]): string => {
  const typeMap = new Map<CopilotThinkingSignatureType, number>()
  thinkingSignatures.forEach((signature) => {
    typeMap.set(signature.type, (typeMap.get(signature.type) ?? 0) + 1)
  })

  return Array.from(typeMap.entries())
    .map(([type, count]) => {
      const labels = THINKING_TYPE_TO_SUMMARY_TEXT[type]
      return `${count} ${count === 1 ? labels.singular : labels.plural}`
    })
    .join(', ')
}

export const getActionGroupSummary = (
  groupType: ActionGroupType,
  actions: CardActionSignature[]
): { title: string; label: string } => {
  const config = ACTION_GROUP_CONFIG[groupType]
  const isInProgress = actions.some((action) => action.isInProgress === true)

  return {
    title: isInProgress ? config.inProgressTitle : config.completeTitle,
    label: actions.length === 1 ? config.singular : config.plural
  }
}

export const getThinkingSignatureRenderItemKey = (item: ThinkingSignatureRenderItem): string => {
  if (item.kind === 'signature') {
    return item.signature.id
  }

  return `${ACTION_GROUP_CONFIG[item.groupType].keyPrefix}-${item.actions.map((action) => action.id).join('-')}`
}
